import { createStyles, Group, Menu, Stack, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import type {
	CatalogRule,
	PolicyRuleIn,
	PolicyRuleType,
} from '@repo/api-codegen';
import { Banner, Button, Text, Title } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import {
	useCreatePolicyRule,
	useDeletePolicyRule,
	useListPolicyRules,
	useUpdatePolicyRule,
} from '../../../api/hooks/policy';
import { useCustomFilterOptionsConfig } from '../../../components/Filter/useCustomFilterOptionsConfig';
import { FilterBlockController } from '../../../features/FilterBlock';
import { useDebouncedCallbackWithFlush } from '../../../hooks/useDebouncedCallbackWithFlush';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { getFilterOptionsForPolicy } from '../utils/policyFilterOptions';
import PIIClassifierBanner from './PiiClassifierBanner';
import { PiiClassifierStack } from './PiiClassifierStack';
import PolicyConditionHeader from './PolicyConditionHeader';
import { useCreatePolicyStore } from './useCreatePolicyStore';

const useStyles = createStyles(() => ({
	group: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'baseline',
	},
	dropdown: {
		width: '160px',
		minWidth: '160px',
	},
}));

interface PolicyRulesStackProps {
	policyId?: string;
}

const PolicyRulesStack = observer(
	({ policyId }: PolicyRulesStackProps = {}) => {
		const { classes } = useStyles();
		const store = useCreatePolicyStore();
		const customOptionsConfig = useCustomFilterOptionsConfig();
		const { dqsV2, dataScanning } = useFeatureFlags();
		const [opened, { close, open, toggle }] = useDisclosure(false);
		const [showPIIClassifierBanner, setShowPIIClassifierBanner] = useState(
			store.displayPiiClassifierBanner
		);

		// React Query hooks for server operations
		const { isLoading } = useListPolicyRules(
			{ pathParams: { policyId: policyId! } },
			{
				enabled: !!policyId,
			}
		);

		const { mutateAsync: createRule } = useCreatePolicyRule();
		const { mutateAsync: updateRule } = useUpdatePolicyRule();
		const { mutateAsync: deleteRule } = useDeletePolicyRule();

		// Load existing rules when in edit mode - handled by store initialization

		const filterOptions = useMemo(
			() => getFilterOptionsForPolicy(customOptionsConfig, store.dqsEnabled),
			[customOptionsConfig, store.dqsEnabled]
		);

		const addPolicyRule = async (type: PolicyRuleType) => {
			const newPolicyRule: PolicyRuleIn = {
				name: `Rule ${store.policyRules.length + 1}`,
				description: '',
				weight: 1,
				points_possible: 10,
				type,

				filter: {
					operator: 'and',
					operands: [
						{
							operator: 'and',
							operands: [],
						},
					],
				},
				classifiers: [],
			};

			// If we're in edit mode, create the rule on the server immediately
			if (policyId) {
				try {
					const createdRule = await createRule({
						pathParams: { policyId },
						body: newPolicyRule,
					});

					// Add the created rule to the store with its ID
					store.addPolicyRule(newPolicyRule);

					// Update the initial rules to include the new rule
					store.updateInitialPolicyRules([
						...store.initialPolicyRules,
						createdRule,
					]);

					showNotification({
						title: 'Success',
						message: 'Policy rule created successfully.',
						color: 'green',
					});
				} catch {
					showNotification({
						title: 'Error',
						message: 'Failed to create policy rule.',
						color: 'red',
					});
				}
			} else {
				// In create mode, just add to the store
				if (type === 'PII_SCAN') {
					store.setPIIClassifierAdded(true);
				}
				store.addPolicyRule(newPolicyRule);
			}
		};

		const removePolicyRule = async (
			index: number,
			isPIIClassifier: boolean = false
		) => {
			if (isPIIClassifier) {
				store.setPIIClassifierAdded(false);
			}
			const existingRule = store.initialPolicyRules[index];

			// If this is an existing rule and we're in edit mode, delete from server
			if (existingRule?.id && policyId) {
				try {
					await deleteRule({
						pathParams: {
							policyId: policyId,
							ruleId: existingRule.id,
						},
					});
					showNotification({
						title: 'Success',
						message: 'Policy rule deleted successfully.',
						color: 'green',
					});
				} catch {
					showNotification({
						title: 'Error',
						message: 'Failed to delete policy rule.',
						color: 'red',
					});
					return; // Don't remove from store if server deletion failed
				}
			}

			store.removePolicyRule(index);
		};

		// Debounced server update function
		const debouncedServerUpdate = useDebouncedCallbackWithFlush(
			async (index: number, updatedRule: PolicyRuleIn) => {
				const existingRule = store.initialPolicyRules[index];
				if (existingRule?.id && policyId) {
					try {
						await updateRule({
							pathParams: {
								policyId: policyId,
								ruleId: existingRule.id,
							},
							body: updatedRule,
						});
					} catch {
						showNotification({
							title: 'Error',
							message: 'Failed to update policy rule.',
							color: 'red',
						});
					}
				}
			},
			500 // 500ms debounce delay
		);

		const updatePolicyRuleFilter = useCallback(
			(index: number, filter: CatalogRule) => {
				const updatedRule = { ...store.policyRules[index], filter };

				// Update store immediately for responsive UI
				store.updatePolicyRule(index, updatedRule);

				// Debounce server update to avoid jumping during filter editing
				debouncedServerUpdate(index, updatedRule);
			},
			[store, debouncedServerUpdate]
		);

		if (isLoading || store.isLoadingPolicyRules) {
			return (
				<Stack spacing="md">
					<Stack spacing={0}>
						<Title size="md">Policy Rules</Title>
						<Text size="sm" color="text/secondary/default">
							Loading policy rules...
						</Text>
					</Stack>
				</Stack>
			);
		}

		return (
			<Stack spacing="md">
				<Group className={classes.group}>
					<Stack spacing={0}>
						<Title size="md">Conditions</Title>
						<Text size="sm" color="text/secondary/default">
							An issue is triggered if conditions are not met.{' '}
							{dqsV2 &&
								store.dqsEnabled &&
								'Set a condition and pair it with an impact score to control how much fixing this issue will contribute to the total DQS.'}
						</Text>
					</Stack>
					{!dataScanning && (
						<Button
							variant="default"
							onClick={() => addPolicyRule('METADATA')}
							leftIconName="plus"
						>
							Add condition
						</Button>
					)}
					{dataScanning && (
						<Menu
							opened={opened}
							onClose={close}
							onOpen={open}
							classNames={{ dropdown: classes.dropdown }}
						>
							<Menu.Target>
								<Button
									variant="default"
									onClick={toggle}
									leftIconName="plus"
									rightIconName="chevronDown"
								>
									Add condition
								</Button>
							</Menu.Target>
							<Menu.Dropdown>
								<Menu.Item onClick={() => addPolicyRule('METADATA')}>
									Metadata condition
								</Menu.Item>
								<Tooltip
									label="One PII classifier condition per policy"
									withinPortal
									disabled={!store.piiClassifierAdded}
								>
									<div>
										<Menu.Item
											onClick={() => addPolicyRule('PII_SCAN')}
											disabled={store.piiClassifierAdded}
											sx={(theme) => ({
												'&:disabled .mantine-Menu-itemLabel': {
													color: theme.other.getColor('text/primary/disabled'),
												},
											})}
										>
											PII classifier
										</Menu.Item>
									</div>
								</Tooltip>
							</Menu.Dropdown>
						</Menu>
					)}
				</Group>

				{store.policyRules.map((rule, index) => {
					// Use existing rule ID if available, otherwise use a stable index-based key
					const key =
						store.initialPolicyRules[index]?.id || `new-rule-${index}`;

					if (
						rule.type === 'PII_SCAN' &&
						dataScanning &&
						store.piiClassifierAdded
					) {
						return (
							<>
								<PiiClassifierStack
									key={key}
									store={store}
									index={index}
									removePolicyRule={removePolicyRule}
								/>
								{showPIIClassifierBanner && (
									<PIIClassifierBanner
										store={store}
										onDismiss={() => setShowPIIClassifierBanner(false)}
									/>
								)}
							</>
						);
					}

					return (
						<Stack spacing="md" key={key}>
							<FilterBlockController
								initialRule={rule.filter as CatalogRule}
								onRuleChange={(filter: CatalogRule) =>
									updatePolicyRuleFilter(index, filter)
								}
								filterOptions={filterOptions}
								displayPreview={false}
								errorOnEmpty={true}
								hideFilterRemoveButton={true}
								customHeader={
									<PolicyConditionHeader
										rule={rule}
										removePolicyRule={removePolicyRule}
										index={index}
										dqsV2={dqsV2}
										store={store}
										debouncedServerUpdate={debouncedServerUpdate}
									/>
								}
							/>
						</Stack>
					);
				})}

				{store.policyRulesError && (
					<Banner tone="critical" message={store.policyRulesError} inCard />
				)}
			</Stack>
		);
	}
);

PolicyRulesStack.displayName = 'PolicyRulesStack';
export default PolicyRulesStack;
export type { PolicyRulesStackProps };
